import { useQuery } from '@apollo/client';
import useSWR from 'swr';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import GraphQLErrorText from '../../../components/GraphQLErrorText';
import Loading from '../../../components/Loading';
import TidalDropdown from '../../../components/common/dropdown/TidalDropdown';
import CopyCross from '../../../components/svgs/CopyCross';
import CopyTick from '../../../components/svgs/CopyTick';
import PaperPlane from '../../../components/svgs/PaperPlane';
import TidalDashboardHeader from '../../../components/tidal-dashboard/common/header';
import TidalDashboardWebhookAnalyticsTable from '../../../components/tidal-dashboard/webhooks/TidalDashboardWebhookAnalyticsTable';
import WebhookGraph from '../../../components/tidal-dashboard/webhooks/TidalDashboardWebhookGraph';
import TidalDashboardWebhookOrderHistory from '../../../components/tidal-dashboard/webhooks/TidalDashboardWebhookOrderHistory';
import TidalDashboardWebhooksHeader from '../../../components/tidal-dashboard/webhooks/TidalDashboardWebhooksHeader';
import useAuthOrRedirect from '../../../hooks/useAuthOrRedirect';
import { PROFILE } from '../../../lib/graphql/queries';
import { DateTime } from 'luxon';

const TidalDashboardWebhooksWrapper = styled.div`
  width: 100%;
`;

const TidalDashboardWebhooksFilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 0 25px;
  margin: 25px 0px;
  @media only screen and (max-width: 786px) {
    grid-template-columns: 1fr;
    grid-gap: 8px 0;
  }
`;

const TidalDashboardWebhooksChartRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 0 25px;
  margin: 25px 0px;
  @media only screen and (max-width: 786px) {
    grid-template-columns: 1fr;
    grid-gap: 8px 0;
  }
`;

const TidalDashboardWebhooksTableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 0 25px;
  margin: 35px 0px;
  max-width: 100%;
  @media only screen and (max-width: 786px) {
    grid-template-columns: 1fr;
    grid-gap: 8px 0;
  }
`;

const TidalDashboardWebhooksOrderHistoryTableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 0 25px;
  margin: 35px 0px;
  max-width: 100%;
  @media only screen and (max-width: 786px) {
    grid-gap: 8px 0;
  }
`;

const TidalDashboardWebhooks = (): JSX.Element => {
  const { loading, error } = useQuery(PROFILE);
  const {
    data,
    error: err,
    isValidating,
    mutate,
  } = useSWR('/dashboard/webhooks/data');

  const [siteFilter, setSiteFilter] = useState('All');
  const [botFilter, setBotFilter] = useState('All');

  const [timeframeFilter, setTimeframeFilter] = useState('All');

  const {
    data: graphData,
    error: graphError,
    isValidating: isGraphDataValidating,
    mutate: graphMutation,
  } = useSWR(
    `/dashboard/webhooks/chart/data?interval=${
      timeframeFilter == 'All' ? 'all' : timeframeFilter.toLowerCase()
    }&site=${siteFilter == 'All' ? 'all' : siteFilter.toLowerCase()}&software=${
      botFilter == 'All' ? 'all' : botFilter.toLowerCase()
    }`,
  );

  const {
    data: orderData,
    error: orderErrorError,
    isValidating: isOrderDataValidating,
    mutate: orderMutation,
  } = useSWR('/dashboard/webhooks/payloads?page=1&limit=100');

  const [sites, setSites] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [timeframes, setTimeframes] = useState(['All', '7d', '6m', '1m']);

  const [bots, setBots] = useState([]);
  const [sentCount, setSentCount] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);

  const [webhookSentCount, setWebhookSentCount] = useState(0);
  const [webhookSuccessCount, setWebhookSuccessCount] = useState(0);
  const [webhookFailedCount, setWebhookFailedCount] = useState(0);

  const [sitesData, setSitesData] = useState([]);
  const [botsData, setBotsData] = useState([]);
  const [sizesData, setSizesData] = useState([]);

  const [allSoftwares, setAllSoftwares] = useState([]);
  const [allSites, setAllSites] = useState([]);

  const [allData, setAllData] = useState([]);

  const [currentSuccessChartData, setCurrentSuccessChartData] = useState<any>();
  const [currenFailedChartData, setCurrentFailedChartData] = useState<any>();
  const [currentSentChartData, setCurrentSentChartData] = useState<any>();

  useEffect(() => {
    if (graphData && graphData?.data) {
      setSitesData(graphData?.data?.siteTotals);
      setBotsData(graphData?.data?.softwareTotals);
      setSizesData(graphData?.data?.sizeTotals);
      setAllData(graphData?.data?.allTotals);
      if (allSoftwares.length == 0) {
        setAllSoftwares([
          'All',
          ...Object.keys(graphData?.data?.softwareTotals),
        ]);
      }
      if (allSites.length == 0) {
        setAllSites(['All', ...Object.keys(graphData?.data?.siteTotals)]);
      }
      setSites(['All', ...Object.keys(graphData?.data?.siteTotals)]);
      setBots(['All', ...Object.keys(graphData?.data?.softwareTotals)]);
      setSizes(['All', ...Object.keys(graphData?.data?.sizeTotals)]);

      console.log(graphData?.data);

      const countData: any = Object.values(graphData?.data?.allTotals).reduce(
        (totals: any, current: any) => {
          totals.total += current.total;
          totals.success += current.success;
          totals.failed += current.failed;
          return totals;
        },
        { total: 0, success: 0, failed: 0 },
      );

      setWebhookSentCount(countData?.total);
      setWebhookSuccessCount(countData?.success);
      setWebhookFailedCount(countData?.failed);

      const botTotals = Object.keys(graphData?.data?.softwareTotals).map(
        (key) => {
          return Object.values(graphData?.data?.softwareTotals[key]).reduce(
            (totals: any, current: any) => {
              totals.total += current.total;
              totals.success += current.success;
              totals.failed += current.failed;
              return totals;
            },
            { total: 0, success: 0, failed: 0, name: key },
          );
        },
      );

      setBotsData(botTotals);

      const sizesTotal = Object.keys(graphData?.data?.sizeTotals).map((key) => {
        return Object.values(graphData?.data?.sizeTotals[key]).reduce(
          (totals: any, current: any) => {
            totals.total += current.total;
            totals.success += current.success;
            totals.failed += current.failed;
            return totals;
          },
          { total: 0, success: 0, failed: 0, name: key },
        );
      });

      setSizesData(sizesTotal);

      const sitesTotal = Object.keys(graphData?.data?.siteTotals).map((key) => {
        return Object.values(graphData?.data?.siteTotals[key]).reduce(
          (totals: any, current: any) => {
            totals.total += current.total;
            totals.success += current.success;
            totals.failed += current.failed;
            return totals;
          },
          { total: 0, success: 0, failed: 0, name: key },
        );
      });

      setSitesData(sitesTotal);

      // setting default chart state and data
      const x_axis = Object.keys(graphData?.data?.allTotals).map((t) =>
        moment(new Date(t)),
      );

      const y_axis_total = Object.values(graphData?.data?.allTotals)?.map(
        (d: any) => d.total,
      );
      const y_axis_success = Object.values(graphData?.data?.allTotals)?.map(
        (d: any) => d.success,
      );
      const y_axis_failed = Object.values(graphData?.data?.allTotals)?.map(
        (d: any) => d.failed,
      );

      setCurrentSentChartData({ x: x_axis, y: y_axis_total });
      setCurrentFailedChartData({ x: x_axis, y: y_axis_failed });
      setCurrentSuccessChartData({ x: x_axis, y: y_axis_success });
    }
  }, [graphData]);

  useEffect(() => {
    if (data && typeof data == 'object') {
      setSentCount(data?.userTotals);
      setSuccessCount(data?.userSuccess);
      setFailedCount(data?.userFail);
    }
  }, [data]);

  useEffect(() => {
    console.log(botFilter);
    mutate(
      `/webhooks/chart/data?interval=${
        timeframeFilter == 'All' ? 'all' : timeframeFilter.toLowerCase()
      }&site=${
        siteFilter == 'All' ? 'all' : siteFilter.toLowerCase()
      }&software=${botFilter == 'All' ? 'all' : botFilter.toLowerCase()}`,
    );
  }, [botFilter, siteFilter, timeframeFilter]);

  const filterChartData = (type, value) => {
    switch (type) {
      case 'bots':
        setBotFilter(value);
        break;
      case 'sites':
        setSiteFilter(value);
        break;
      case 'timeframe':
        setTimeframeFilter(value);
        break;
    }
  };

  const failedRef = useRef();
  const router = useRouter();

  const [statsFilter, setStatsFilter] = useState({
    botId: null,
    timeframe: 'All Time',
    site: null,
  });

  if (!useAuthOrRedirect()) return null;
  if (
    loading ||
    (!data && !err) ||
    // (!graphData && !graphError) ||
    (!orderData && !orderErrorError)
  )
    return <Loading />;
  if (error) return <GraphQLErrorText error={error} />;

  return (
    <TidalDashboardWebhooksWrapper>
      <TidalDashboardHeader
        title="Webhooks"
        showSettings={true}
        tags={[]}
        onSettingsClick={() => {
          router.push('/tidal-dashboard/webhooks/settings');
        }}
      />
      <TidalDashboardWebhooksHeader
        title="Global Stats from Tidal Users"
        items={[
          {
            type: 0,
            text: 'Webhooks Sent',
            count: data?.globalTotals,
            Icon: PaperPlane,
          },
          {
            type: 1,
            text: 'Successful Checkouts',
            count: data?.globalSuccess,
            Icon: CopyTick,
          },
          {
            type: 2,
            text: 'Failed Checkouts',
            count: data?.globalFail,
            Icon: CopyCross,
          },
        ]}
      />
      <TidalDashboardWebhooksFilterRow>
        <TidalDropdown
          label="Site"
          items={allSites}
          onSelect={(e) => {
            filterChartData('sites', e);
          }}
        />
        <TidalDropdown
          label="Bot"
          items={allSoftwares}
          onSelect={(e) => {
            filterChartData('bots', e);
          }}
        />
        <TidalDropdown
          label="Timeframe"
          items={timeframes}
          onSelect={(e) => {
            filterChartData('timeframe', e);
          }}
        />
      </TidalDashboardWebhooksFilterRow>
      <TidalDashboardWebhooksChartRow>
        {currentSentChartData && (
          <WebhookGraph
            ref={failedRef}
            type="Webhooks sent: "
            color="#1CBAF0"
            x_ordinates={currentSentChartData?.x}
            y_ordinates={currentSentChartData?.y}
            timeframe={statsFilter.timeframe}
            Icon={PaperPlane}
            count={webhookSentCount}
            text="Webhooks Sent"
          />
        )}
        {currentSuccessChartData && (
          <WebhookGraph
            ref={failedRef}
            type="Succesful checkouts: "
            color="#23E588"
            x_ordinates={currentSuccessChartData?.x}
            y_ordinates={currentSuccessChartData?.y}
            timeframe={statsFilter.timeframe}
            Icon={CopyTick}
            count={webhookSuccessCount}
            text="Succesful Checkouts"
          />
        )}
        {currenFailedChartData && (
          <WebhookGraph
            ref={failedRef}
            type="Failed checkouts: "
            color="#F35C8A"
            x_ordinates={currenFailedChartData?.x}
            y_ordinates={currenFailedChartData?.y}
            timeframe={statsFilter.timeframe}
            Icon={CopyCross}
            count={webhookFailedCount}
            text="Failed Checkouts"
          />
        )}
      </TidalDashboardWebhooksChartRow>
      <TidalDashboardWebhooksTableRow>
        <TidalDashboardWebhookAnalyticsTable
          columns={['Site', 'Webhooks', 'Success', 'Fails']}
          data={sitesData}
          label="Analytics by Site"
        />
        <TidalDashboardWebhookAnalyticsTable
          columns={['Bot', 'Webhooks', 'Success', 'Fails']}
          data={botsData}
          label="Analytics by Bot"
        />
        <TidalDashboardWebhookAnalyticsTable
          columns={['Size', 'Webhooks', 'Success', 'Fails']}
          data={sizesData}
          label="Analytics by Size"
        />
      </TidalDashboardWebhooksTableRow>
      <TidalDashboardWebhooksOrderHistoryTableRow>
        <TidalDashboardWebhookOrderHistory
          columns={['Site', 'Product', 'Size', 'Email', 'Order Number']}
          data={orderData}
          label="Order History"
        />
      </TidalDashboardWebhooksOrderHistoryTableRow>
    </TidalDashboardWebhooksWrapper>
  );
};

export default TidalDashboardWebhooks;
